import { call, cancelled, put, select } from 'redux-saga/effects'
import { selectAuthData, selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import getAPIConfig from '@/shared/tools/getAPIConfig'
import {
  addAddressError,
  removeAddressError,
  resetAddressDirty,
  updateAddressData,
  updateAddressesConfigData,
  updateContractConfigData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { t } from 'i18next'
import {
  isProfessionalContact,
  selectActiveAddress,
  selectActiveTabId,
  selectConfig,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { selectAddressesConfigData, selectCurrentAddressData } from '../../SubscriptionForm/SubscriptionFormSelectors'
import { resetAddressData } from '../../SubscriptionForm/SubscriptionFormSlice'
import { modulesConfig } from '@/containers/SubscriptionFormPageNew/config'
import { getGasAndElectricityCityAvailability } from '@/shared/tools/addressModuleHelper'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { BUSINESS_VERTICAL_ENERGY } from '../../SubscriptionForm/Common/constants'
import { loadOffers } from '@/containers/SubscriptionFormPageNew/Components/Contract/ContractSagas'
import { showSnackbar, SNACKBAR_TYPES } from '../../../../shared/tools/notifications/notification'
import { buildCarPayload } from './buildCarEstimation'
import * as Sentry from '@sentry/react'

export const addressModuleActions = {
  SEARCH_ADDRESS_V2: 'SEARCH_ADDRESS_V2',
  CITY_ELIGIBILITY_CHECK_V2: 'CITY_ELIGIBILITY_CHECK_V2',
  GET_PLACE_DETAILS: 'GET_PLACE_DETAILS',
  GET_COMPARATOR_CITY_ID: 'GET_COMPARATOR_CITY_ID',
  SAVE_ADDRESS: 'SAVE_ADDRESS',
  CALCULATE_CONSUMPTION: 'CALCULATE_CONSUMPTION',
}

export function* searchAddress({ address }) {
  yield put(updateAddressesConfigData({ addressIsLoading: true }))
  const controller = new AbortController()
  const fullUrl = `https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(address)}`

  try {
    const response = yield call(fetch, fullUrl, { signal: controller.signal })
    yield put(updateAddressesConfigData({ caption: '', addressIsLoading: false }))
    const responseData = yield call([response, response.json])
    if (response.status !== 200) {
      yield put(updateAddressesConfigData({ addressList: [] }))
      return
    }
    const addressList = responseData.features.map(result => {
      const isMunicipality = result.properties.type === 'municipality'
      const streetName = result.properties.street ?? (isMunicipality ? '' : (result.properties.name ?? ''))
      return {
        streetName,
        label: result.properties.label,
        city: result.properties.city,
        inseeCode: result.properties.citycode,
        zipcode: result.properties.postcode,
        houseNumber: result.properties.housenumber,
        type: result.properties.type,
        context: result.properties.context,
      }
    })
    yield put(updateAddressesConfigData({ addressList }))
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('Error searching address'), { extra: { error } })
    }
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

export function* cityEligibilityCheck({
  inseeCode,
  addressId,
  offersParams,
  loadOffersUrl,
  businessVerticalType,
  loadOfferConfigurationUrl,
  activeTab = 'contract',
}) {
  const { lang } = yield select(selectAuthData)
  const { eldCheckUrl: url } = yield select(selectExtraRoutesAuthData)
  const contractId = yield select(selectActiveTabId)
  const fullUrl = `${url}?lang=${lang}&city=${encodeURIComponent(inseeCode)}&energy=dual`
  yield put(
    updateAddressesConfigData({
      addressCityEligibilityIsLoading: true,
    }),
  )

  try {
    const cityEligibility = yield call(callApi, {
      endpoint: fullUrl,
      showErrorSnackbar: false,
      method: 'GET',
    })

    yield put(
      updateAddressesConfigData({
        addressCityEligibilityIsLoading: false,
      }),
    )

    const { gasAvailable, electricityAvailable, energyTypeDisabledOptions } = yield call(
      getGasAndElectricityCityAvailability,
      cityEligibility,
    )

    yield put(
      updateAddressData({
        id: addressId,
        data: { cityEligibility, gasAvailable, electricityAvailable },
        _skipDirty: true, // background eligibility check, not a user edit
      }),
    )

    if (activeTab === 'client') {
      return
    }

    yield put(
      updateContractConfigData({
        data: { energyTypeDisabledOptions },
        id: contractId,
      }),
    )

    if (businessVerticalType?.toLowerCase() === BUSINESS_VERTICAL_ENERGY) {
      yield call(loadOffers, { params: offersParams, id: contractId, loadOfferConfigurationUrl, loadOffersUrl })
    }
  } catch (error) {
    yield put(
      updateAddressData({
        id: addressId,
        data: { cityEligibility: {}, gasAvailable: false, electricityAvailable: false },
        _skipDirty: true, // background eligibility reset, not a user edit
      }),
    )
    yield put(
      updateAddressesConfigData({
        addressCityEligibilityIsLoading: false,
      }),
    )
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('Error in city eligibility check'), { extra: { error } })
    }
  }
}

export function* calculateConsumption() {
  const { estimateConsumptionUrl } = yield select(selectExtraRoutesAuthData)
  const { contactId } = yield select(selectConfig)
  const addressData = yield select(selectCurrentAddressData)

  yield put(
    updateAddressesConfigData({
      isCalculatingConsumption: true,
    }),
  )

  try {
    const response = yield call(callApi, {
      endpoint: estimateConsumptionUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload: buildCarPayload({ addressData, contactId }),
    })

    yield put(
      updateAddressesConfigData({
        isCalculatingConsumption: false,
      }),
    )
    yield put(
      updateAddressData({
        id: addressData.id,
        data: {
          energyLandscape: {
            ...addressData.energyLandscape,
            consumptionEstimation: response,
          },
        },
        _skipDirty: true, // background consumption estimation, not a user edit
      }),
    )
  } catch (error) {
    yield put(
      updateAddressesConfigData({
        isCalculatingConsumption: false,
      }),
    )
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('Error calculating consumption estimation'), { extra: { error } })
    }
  }
}

// Returns { success: boolean, addressId: number | null } so callers can
// reliably tell whether the address was actually persisted by the backend.
// Used by saveClientTab to decide whether to skip the meter saga's internal
// saveAddress fallback (avoids duplicate address creation).
export function* saveAddress({ enqueue = null, successMessage = '' }) {
  const controller = new AbortController()
  const { authTokenSession, country } = yield select(selectAuthData)
  const { saveAddressUrl } = yield select(selectExtraRoutesAuthData)
  const { contactId } = yield select(selectConfig)
  const { errors } = yield select(selectAddressesConfigData)
  const addressData = yield select(selectCurrentAddressData)
  const activeAddress = yield select(selectActiveAddress)
  const isProfessional = yield select(isProfessionalContact)
  yield put(updateAddressesConfigData({ errors: { ...errors, [activeAddress]: {} } }))
  yield put(updateAddressesConfigData({ addressIsSaving: true }))

  try {
    const config = yield call(getAPIConfig, {
      method: 'post',
      authTokenSession,
      body: { ...addressData, country, contactId, lang: country, isProfessional },
    })
    const signal = controller.signal
    const response = yield call(fetch, saveAddressUrl, { ...config, signal })
    const isValid = response.status === 200
    const responseData = yield call([response, response.json])
    const { id } = responseData
    if (!isValid) {
      if (import.meta.env.PROD && response.status !== 422) {
        Sentry.captureException(new Error('Error saving address'), { extra: { responseData, status: response.status } })
      }
      if (response.status === 422) {
        yield put(addAddressError({ data: responseData.errors ?? {}, id: activeAddress }))
      }
      // Surface the failure to the user. Previously this was silent, which
      // was confusing in the global save flow (contact succeeded, address
      // failed → only the success snackbar showed up).
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.ERROR,
          message:
            response.status === 422
              ? t('subscriptionForm:snackbar.address_save.validation_error')
              : t('subscriptionForm:snackbar.address_save.save_error'),
        })
      }
      return { success: false, addressId: null }
    }
    if ((activeAddress === 'new' || activeAddress === null) && id > 0) {
      // Copy persisted data into the real-id bucket. Mark _skipDirty so the
      // newly persisted bucket isn't immediately re-flagged as unsaved.
      yield put(updateAddressData({ data: { ...addressData, id }, id, _skipDirty: true }))
      yield put(resetAddressData({ id: activeAddress }))
      // Save promoted the 'new' draft to a persisted id: clean both buckets.
      yield put(resetAddressDirty({ id: activeAddress }))
      yield put(resetAddressDirty({ id }))
    } else {
      yield put(resetAddressDirty({ id: activeAddress }))
    }
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message: successMessage,
      })
    }
    return { success: true, addressId: id > 0 ? id : activeAddress }
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('Error saving address'), { extra: { error } })
    }
    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: t('subscriptionForm:snackbar.address_save.save_error'),
      })
    }
    return { success: false, addressId: null }
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
    yield put(updateAddressesConfigData({ addressIsSaving: false }))
  }
}

export function* getPlaceDetails({ address }) {
  const controller = new AbortController()
  const { label } = address
  const placeId = address.value.place_id
  const { country } = yield select(selectAuthData)
  const { getPlaceDetailsUrl } = yield select(selectExtraRoutesAuthData)
  const activeAddress = yield select(selectActiveAddress)
  const fullUrl = `${getPlaceDetailsUrl}?placeId=${placeId}&country=${country}`

  try {
    const response = yield call(fetch, fullUrl, { signal: controller.signal })
    const responseData = yield call([response, response.json])
    yield put(updateAddressesConfigData({ caption: '' }))
    const newAddress = {
      streetName: responseData.street,
      label,
      city: responseData.locality_2 || responseData.locality,
      province: responseData.province,
      zipcode: responseData.postal_code,
      id: '',
      streetNumber: responseData.street_number,
    }
    const currentAddress = yield select(selectActiveAddress)

    if (responseData.postal_code && responseData.postal_code.length > 0) {
      yield put(removeAddressError({ id: activeAddress, key: 'addressType' }))
      const address = yield call(getComparatorCityId, { address: newAddress })
      yield put(updateAddressData({ data: address, id: currentAddress }))
      return
    }
    yield put(updateAddressData({ data: {}, id: currentAddress }))
    yield put(
      updateAddressesConfigData({
        caption: t('subscriptionForm:address_module.address_invalid'),
      }),
    )
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('Error getting place details'), { extra: { error } })
    }
    yield put(
      updateAddressesConfigData({
        caption: t('subscriptionForm:address_module.address_error'),
      }),
    )
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

export function* getComparatorCityId({ address }) {
  const controller = new AbortController()
  const { country } = yield select(selectAuthData)
  const activeAddress = yield select(selectActiveAddress)
  const { zipcode } = address
  const comparatorId = modulesConfig[country].commonData.comparatorId

  if (!comparatorId) {
    return address
  }

  const fullUrl = `https://energy-core.app.production.aws.selectra.io/api/load-cities?term=${zipcode}&site_id=${comparatorId}`

  try {
    const response = yield call(fetch, fullUrl, { signal: controller.signal })
    const responseData = yield call([response, response.json])
    const cities = responseData.data

    if (!cities.length) {
      return
    }

    if (cities.length === 1) {
      address.cityId = cities[0].id
      return address
    }

    const cityFilterByName = cities.filter(city => city.name === address.city)

    if (!cityFilterByName.length) {
      yield put(
        addAddressError({
          data: { addressType: [t('subscriptionForm:address_module.address_invalid')] },
          id: activeAddress,
        }),
      )
    }

    address.cityId = cityFilterByName[0].id

    return address
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('Error searching address'), { extra: { error } })
    }
    yield put(
      updateAddressesConfigData({
        caption: t('subscriptionForm:address_module.address_error'),
      }),
    )
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}
